import { CartAPI } from '../api/cart';
import { PRE_REQUEST_IDS, QUERY_STRING_KEY } from '../constants/app';
import { KEY_DYNAMIC_BUNDLE_DATA_SESSION } from '../dynamicBundle';
import {
  buildUrlWithLocalization,
  getLocalStorage,
  getSessionStorage,
  getTenantId,
  isCartPage,
  isCollectionPage,
  isHomePage,
  isProductPage,
  isSearchPage,
  removeLocalStorage,
  setLocalStorage,
  setSessionStorage,
} from '../utils';
import { isAttentiveActive } from '../utils/attentive';
import { isKlaviyoActive } from '../utils/integration';
import { longParamsMap } from '../utils/params.js';
import {
  ACTION,
  ANALYTICS_URL,
  BOOST_CART_TOKEN,
  BOOST_SD_PRODUCT_IDS_CLICKED,
  DATA_NOT_SEND,
  DEFAULT_SELECTOR,
  DYNAMIC_BUNDLE_TYPE,
  excludeAddToCartOurApp,
  KLAVIYO_BOOST_EVENT,
  MAP_PATH_CLICK,
  PLACEMENT,
  PopupBundlePlacement,
  PRE_ACTION,
  prefix,
  RECOMMEND_ACTIVE,
  REQUEST_ID_PRE_ORDER_VIEW,
  SHOPIFY_CART_TOKEN,
  USER_ACTION,
} from './constants';

/**
 * @type {import('./index.d').getExtraData}
 */
export function getExtraData() {
  return {
    ANALYTICS_URL,
    productIdsClicked: getSessionStorage(BOOST_SD_PRODUCT_IDS_CLICKED) || {},
    cartUrl: window.location.origin + buildUrlWithLocalization('cart.js'),
    newBoostCartToken: generateUUID(BOOST_CART_TOKEN.PREFIX),
    disableBoostCartToken: isDisableBoostCartToken(),
  };
}

/**
 * Generates a random unique session ID
 * @return {string} random unique ID
 */
/**
 * @type {import('./index.d').generateUUID}
 */
export function generateUUID(prefix = 'xxxx') {
  return `${prefix}xxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * @type {import('./index.d').generateRIDPreOrder}
 */
export function generateRIDPreOrder() {
  return 'rid-xxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * @type {import('./index.d').postData}
 */
export async function postData(url = '', data = {}) {
  /***
   * Prevent all event not click from our app
   * not exist requestId -> data.rid => false
   * if productId and userAction is add-to-cart but not click from our widget => false
   * */

  const productIdsClicked = getSessionStorage(BOOST_SD_PRODUCT_IDS_CLICKED) || {};
  if (
    !data.rid ||
    (data.pid && data.u === USER_ACTION.ADD_TO_CART && !productIdsClicked[data.pid])
  ) {
    return;
  }

  data = validateData(data);

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(data), // body data type must match "Content-Type" header
  });
  return response.json(); // parses JSON response into native JavaScript objects
}

/**
 * @type {import('./index.d').isDisableBoostCartToken}
 */
export function isDisableBoostCartToken() {
  // add flag disable boost cart token from config
  return !!window.boostWidgetIntegrationConfig?.analytics?.disableBoostCartToken;
}

/**
 * @type {import('./index.d').createBoostTokenUpdateCartAttribute}
 */
export function createBoostTokenUpdateCartAttribute() {
  let boost_cart_token = null;

  if (!isDisableBoostCartToken()) {
    boost_cart_token = generateUUID(BOOST_CART_TOKEN.PREFIX);
  }

  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      attributes: {
        [BOOST_CART_TOKEN.SHORT_KEY]: boost_cart_token,
      },
    }),
  })
    .then((r) => r.json())
    .then((cart) => {
      setLocalStorage(SHOPIFY_CART_TOKEN, cart.token);
      if (!isDisableBoostCartToken()) {
        setLocalStorage(BOOST_CART_TOKEN.STORAGE_KEY, boost_cart_token);
        console.log('Boost cart token created and updated successfully');
      }
    })
    .catch((error) => {
      console.error('Error creating/updating Boost cart token:', error);
    });
}

let retry = 0;
const maxRetries = 5;

/**
 * @type {import('./index.d').refreshCartTokenSendEvent}
 */
export async function refreshCartTokenSendEvent(data, timeout = 0) {
  try {
    // save data prepare send to localStore
    setLocalStorage(DATA_NOT_SEND, data);
    setTimeout(async () => {
      const toJson = await CartAPI.getAll();
      if (
        !isDisableBoostCartToken() &&
        !toJson?.attributes?.[BOOST_CART_TOKEN.SHORT_KEY] &&
        retry < maxRetries
      ) {
        retry++;
        createBoostTokenUpdateCartAttribute();
        return refreshCartTokenSendEvent(data, 1500);
      }

      data.ct = toJson.token;

      if (toJson?.attributes?.[BOOST_CART_TOKEN.SHORT_KEY]) {
        data.bct = toJson.attributes[BOOST_CART_TOKEN.SHORT_KEY];
      }

      postData(ANALYTICS_URL, data, getExtraData());
      removeLocalStorage(DATA_NOT_SEND);
    }, timeout);
  } catch (error) {
    console.log('Error: refreshCartTokenSendEvent');
  }
}

/**
 * @type {import('./index.d').checkSendEventWhenLoadPage}
 */
export async function checkSendEventWhenLoadPage() {
  try {
    const data = getLocalStorage(DATA_NOT_SEND);
    if (data) {
      const response = await fetch('/cart.js');
      const toJson = await response?.json();

      if (toJson.token) {
        data.ct = toJson.token;

        postData(ANALYTICS_URL, data);
        removeLocalStorage(DATA_NOT_SEND);
      }
    }
  } catch (error) {
    console.log('error checkSendEventWhenLoadPage', error);
  }
}

/**
 * @type {import('./index.d').validateData}
 */
export function validateData(data) {
  // check if productId not correct format => re-assign empty string
  if (data.pid && isNaN(data.pid)) {
    data.pid = '';
  }

  return data;
}

/**
 * @type {import('./index.d').sendEvent}
 */
export function sendEvent(data, activeElement) {
  // Prevent sent event data when view product have not productId - case bundle as product
  if (!data.pid && data.u === USER_ACTION.VIEW_PRODUCT) {
    return;
  }

  if (data.a === ACTION.RECOMMEND) {
    // not send qs when action recommend exclude bundle
    if (!data.actionBundle) {
      data.qs = null;
    }
  }
  if (data.a === ACTION.RECOMMEND) {
    const recommendActive = getLocalStorage(RECOMMEND_ACTIVE) || {};
    data = {
      ...data,
      ...recommendActive,
    };
  }

  setLocalStorage(PRE_ACTION, data.a);

  // prevent all event of widget which widget not come from turbo
  if (!data.rid) return;

  // if cartToken not existed -> get cart -> sendEvent
  if ([USER_ACTION.ADD_TO_CART, USER_ACTION.ATC_PRE_ORDER].includes(data.u)) {
    refreshCartTokenSendEvent(data);
  } else {
    postData(ANALYTICS_URL, data);
  }
}

/**
 * @type {import('./index.d').getCustomerId}
 */
export function getCustomerId() {
  // https://community.shopify.com/c/shopify-apis-and-sdks/current-best-way-to-get-logged-in-customer-id/td-p/482773
  return (
    window?.__st?.cid ||
    window?.meta?.page?.customerId ||
    window?.ShopifyAnalytics?.meta?.page?.customerId ||
    window?.ShopifyAnalytics?.lib?.user?.()?.traits?.()?.uniqToken
  );
}

/**
 * @type {import('./index.d').getParamsHistory}
 */
export function getParamsHistory(href) {
  const url = new URL(href || window.location.href);
  const raw = url.search.replace(/^\?/, '');
  const pairs = raw ? raw.split('&') : [];
  const FILTER_KEY_PREFIX = 'pf_';

  const rawObj = {};
  for (const pair of pairs) {
    const [rawKey, rawVal = ''] = pair.split('=');
    let key = decodeURIComponent(rawKey);
    if (longParamsMap.has(key)) {
      key = longParamsMap.get(key);
    }

    const segments = rawVal.includes(',') ? rawVal.split(',') : [rawVal];
    const decoded = segments.map((seg) => decodeURIComponent(seg.replace(/\+/g, ' ')).trim());

    rawObj[key] = (rawObj[key] || []).concat(decoded);
  }

  const paramList = Object.entries(rawObj)
    .filter(([k]) => k.startsWith(FILTER_KEY_PREFIX))
    .map(([key, values]) => ({ key, values }));

  const searchParams = new URLSearchParams(url.search);
  const sort = searchParams.get('sort');
  const page = searchParams.get('page');
  const q = searchParams.get('q');
  const collectionId = window.boostWidgetIntegration?.generalSettings?.collection_id || 0;

  const queryParams = new URLSearchParams();
  if (sort) queryParams.set('sort', sort);
  if (page) queryParams.set('page', page);
  if (q) queryParams.set('q', q);
  queryParams.set('collection_scope', collectionId);

  paramList
    .sort((a, b) => a.key.localeCompare(b.key))
    .forEach(({ key, values }) => {
      values.forEach((v) => queryParams.append(key, v));
    });

  return `${queryParams}`;
}

/**
 * @type {import('./index.d').getCurrentPage}
 */
export function getCurrentPage() {
  let currentPage = 'collection_page';

  switch (true) {
    case isCollectionPage():
      currentPage = 'collection_page';
      break;

    case isSearchPage():
      currentPage = 'search_page';
      break;

    case isProductPage():
      currentPage = 'product_page';
      break;

    case isCartPage():
      currentPage = 'cart_page';
      break;

    case isHomePage():
      currentPage = 'home_page';
      break;

    default:
      break;
  }

  return currentPage;
}

/**
 * @type {import('./index.d').checkViewPreOrder}
 */
export function checkViewPreOrder(pid = 0) {
  let productId = pid;
  if (isProductPage()) {
    productId = window.boostWidgetIntegration?.generalSettings?.product_id || window.__st?.rid;
  }

  if (!productId) return;

  let attempts = 0;
  const maxAttempts = 100;

  const intervalId = setInterval(() => {
    // find element have class boost-sd__preorder-title
    const hasPreOrderBlock = document.querySelector(
      '.boost-sd__preorder-option .boost-sd__preorder-title'
    );

    if (hasPreOrderBlock) {
      clearInterval(intervalId);
      const data = buildGeneralData({ u: USER_ACTION.VIEW_PRODUCT_PRE_ORDER, pid: productId });

      const clicked = getSessionStorage(BOOST_SD_PRODUCT_IDS_CLICKED) || {};
      if (clicked[productId] && clicked[productId] !== 'none') {
        data.rid = clicked[productId];
        data.a = clicked[`${productId}_action`];
      } else {
        data.rid = generateRIDPreOrder();
        setSessionStorage(REQUEST_ID_PRE_ORDER_VIEW, data.rid);
        data.a = 'other';
      }

      sendEvent(data);
    }

    // If not found and re-try maxAttempts -> stop
    attempts++;
    if (attempts >= maxAttempts) {
      clearInterval(intervalId);
    }
  }, 100);
}

/**
 * @type {import('./index.d').initAnalytic}
 */
export function initAnalytic() {
  if (isSearchPage()) {
    setLocalStorage(QUERY_STRING_KEY, getParamsHistory());
  }

  // For Klaviyo
  identifyUser();
  checkSendEventKlaviyoViewCollection();
  addEventKeyPressForSearching();
  // End for Klaviyo

  window.addEventListener('debounce-in-collection-search', sendKlaviyoEventInCollectionSearch);
  window.addEventListener('change-range-slider', sendKlaviyoEventChangeRangeSlider);
  window.addEventListener('re-view-collection', checkSendEventKlaviyoViewCollection);

  checkSendEventWhenLoadPage();
  console.log('Analytic register events - Version SSR');
  document.addEventListener('click', onClickTrackingEvent, true);

  checkViewPreOrder();
}

/**
 * @type {import('./index.d').getVariantIdFromUrl}
 */
export function getVariantIdFromUrl() {
  const url = new URL(window.location.href);
  return url.searchParams.get('variant') || '';
}

/**
 * @type {import('./index.d').validateVariantIdBeforeSent}
 */
export function validateVariantIdBeforeSent(variantId) {
  if (typeof variantId === 'number') {
    variantId = variantId.toString();
  }
  // check variantId have number
  const isNumber = /^\d+$/.test(variantId);
  const isValidLength = variantId.length > 0 && variantId.length <= 19;

  if (isNumber && isValidLength) {
    return variantId;
  }

  return '';
}

/**
 * @type {import('./index.d').saveSessionStorageClicked}
 */
export function saveSessionStorageClicked(productId, requestId, action = 'other') {
  setSessionStorage(BOOST_SD_PRODUCT_IDS_CLICKED, {
    ...(getSessionStorage(BOOST_SD_PRODUCT_IDS_CLICKED) || {}),
    [productId]: requestId,
    [`${productId}_action`]: action,
  });
}

/**
 * @type {import('./index.d').getBundleProductIds}
 */
export function getBundleProductIds(bundleId) {
  if (!bundleId) return '';

  const bundles = getSessionStorage('DynamicBundleDataSession');
  if (
    bundles &&
    bundles[bundleId] &&
    bundles[bundleId]?.bundleProductIds &&
    Array.isArray(bundles[bundleId].bundleProductIds)
  ) {
    return bundles[bundleId].bundleProductIds.join(',');
  }

  return '';
}

/**
 * @type {import('./index.d').buildGeneralData}
 */
export function buildGeneralData(data = {}, option = {}) {
  // getParamsHistory never null
  let query_string = getParamsHistory();
  const requestIds = getLocalStorage(PRE_REQUEST_IDS) || {};
  const productId = data.pid || '';
  const variantId = data.vid && productId !== data.vid ? data.vid : getVariantIdFromUrl();
  const userAction = data.u || USER_ACTION.VIEW_PRODUCT;
  const action = data.a;

  let requestId =
    option?.wid && data.actionBundle
      ? requestIds[`${option.wid}_${option.placement}`]
      : requestIds[action];

  if (userAction === USER_ACTION.VIEW_PRODUCT && requestId) {
    saveSessionStorageClicked(productId, requestId, action);
  }

  if (!requestId && userAction === USER_ACTION.ATC_PRE_ORDER) {
    requestId = getSessionStorage(REQUEST_ID_PRE_ORDER_VIEW) || generateRIDPreOrder();
  }

  if (action === ACTION.SUGGEST || action === ACTION.SEARCH) {
    query_string += `&${getLocalStorage(QUERY_STRING_KEY)}`;
  } else if (action === ACTION.FILTER) {
    query_string = getParamsHistory(document.referrer);
  }

  let bundleProductIds = '';
  if (productId && userAction === USER_ACTION.VIEW_PRODUCT) {
    setSessionStorage(productId, action);
  }

  let bundleInfo = {};
  if (data.actionBundle && option?.wid) {
    bundleProductIds = getBundleProductIds(option?.wid);
    if (productId && userAction === USER_ACTION.VIEW_PRODUCT) {
      setSessionStorage(productId, option);
    }

    try {
      const dynamicBundle = JSON.parse(sessionStorage.DynamicBundleDataSession);
      if (dynamicBundle[option.wid] && isAttentiveActive()) {
        bundleInfo = {
          bundle_price: dynamicBundle[option.wid]?.totalBundlePrice,
          bundle_discount: dynamicBundle[option.wid]?.amountValueDisplay,
        };
      }
    } catch (e) {
      console.log('Error parsing dynamic bundle');
    }
  }

  return {
    tid: window.Shopify.shop || getTenantId(), // tenant_id
    qs: query_string,
    eid: generateUUID(), // event_id (required)
    rid: requestId || '', // request_id (required)
    // ct: null, // cart token only add <=> add_to_cart and buy_now
    pid: productId, // product_id (optional)
    t: new Date().toISOString(), // clicked at (required)
    u: userAction, // user action (required)
    a: action || 'other', // action (required)
    r: document.referrer, // referrer (optional)
    sid: window.boostWidgetIntegration.getSessionId(), // session_id
    cid: getCustomerId(), // customer_id (optional)
    pg: getCurrentPage(), // current page type (required)
    vid: validateVariantIdBeforeSent(variantId), // variant_id (optional)
    bpids: bundleProductIds, // products of bundle (optional)
    ...option,
    bundle_info: bundleInfo,
  };
}

/**
 * @type {import('./index.d').getProductIdFromQuickView}
 */
export function getProductIdFromQuickView(activeElement) {
  if (!activeElement) return null;
  const modalQuickView = activeElement.closest('#boost-sd__modal-quickview');

  if (modalQuickView) {
    if (modalQuickView.dataset.productId) {
      return modalQuickView.dataset.productId;
    } else {
      if (modalQuickView.dataset.product) {
        return JSON.parse(modalQuickView.dataset?.product || '{}')?.id;
      }
    }
  }

  return null;
}

/**
 * @type {import('./index.d').getModalQuickView}
 */
export function getModalQuickView() {
  return document.getElementById('boost-sd__modal-quickview');
}

/**
 * @type {import('./index.d').buildDataDynamicBundle}
 */
export function buildDataDynamicBundle(recommendationElement) {
  const data = {};
  if (recommendationElement?.dataset?.widgetId) {
    data.wid = recommendationElement?.dataset?.widgetId;

    const getSessionDynamicBundleData = getSessionStorage(KEY_DYNAMIC_BUNDLE_DATA_SESSION);
    if (getSessionDynamicBundleData[data.wid]) {
      data.bt = getSessionDynamicBundleData[data.wid]?.bundle_type;

      // data for bundle type AI
      if (data.bt.startsWith('ai')) {
        data.palg = getSessionDynamicBundleData[data.wid]?.primary_algorithm;
        data.salg = getSessionDynamicBundleData[data.wid]?.secondary_algorithm;
      }
    }

    data.rt = getSessionDynamicBundleData[data.wid]?.recommendationType || DYNAMIC_BUNDLE_TYPE;
  }

  return data;
}

/**
 * @type {import('./index.d').isExcludeAddToCart}
 */
export function isExcludeAddToCart(activeElement) {
  return excludeAddToCartOurApp.some((selector) => activeElement.closest(selector));
}

/**
 * @type {import('./index.d').checkActiveElementWhiteList}
 */
export function checkActiveElementWhiteList(activeElement, selectors) {
  if (!activeElement || !Array.isArray(selectors)) return false;

  return selectors?.some((selector) => activeElement.closest(selector));
}

/**
 * @type {import('./index.d').getPlacementBundle}
 */
export function getPlacementBundle(placement) {
  if (!placement) {
    return getSessionStorage(PopupBundlePlacement);
  }

  return placement;
}

/**
 * @type {import('./index.d').onClickTrackingEvent}
 */
export function onClickTrackingEvent(event) {
  if (!event || !event.target) return;
  let recommendData = {};

  const activeElement = event.target;
  let action = isSearchPage() ? ACTION.SEARCH : isCollectionPage ? ACTION.FILTER : '';
  let productId = '';
  let actionBundle = false;
  let placementBundle = '';

  if (isProductPage()) {
    action = getLocalStorage(PRE_ACTION) || ACTION.FILTER;
    productId = window.boostWidgetIntegration?.generalSettings?.product_id || '';
    placementBundle = PLACEMENT.product_page;
  }

  if (isSearchPage()) {
    placementBundle = PLACEMENT.search_page;
  }

  if (activeElement.closest(window.boostAnalytic.suggest || DEFAULT_SELECTOR.suggest)) {
    action = ACTION.SUGGEST;
    placementBundle = PLACEMENT.instant_search;
  }

  if (activeElement.closest(window.boostAnalytic.recommend || DEFAULT_SELECTOR.recommend)) {
    action = ACTION.RECOMMEND;
    const recommendationElement = activeElement.closest(
      window.boostAnalytic.recommend || DEFAULT_SELECTOR.recommend
    );

    if (recommendationElement && recommendationElement.id) {
      recommendData = getLocalStorage(`boostSdRecommend-${recommendationElement.id}`) || {};

      // data recommendation dynamic bundle
      if (activeElement.closest(`.${prefix}recommendation--dynamic`)) {
        actionBundle = true;
        const dataDynamicBundle = buildDataDynamicBundle(recommendationElement);
        recommendData = {
          ...recommendData,
          ...dataDynamicBundle,
          placement: getPlacementBundle(placementBundle),
        };

        const productOfBundle = activeElement.closest(`.${prefix}recommendation-bundle-image-item`);
        // current: only click image or title then count click product bundle
        const checkWhiteListCountAsProductBundle = checkActiveElementWhiteList(activeElement, [
          `.boost-sd__recommendation-bundle-item-title`,
          `.boost-sd__recommendation-bundle-link-image`,
        ]);
        if (
          productOfBundle &&
          productOfBundle?.dataset?.productId &&
          checkWhiteListCountAsProductBundle
        ) {
          productId = productOfBundle?.dataset?.productId;
        }
      }

      setLocalStorage(RECOMMEND_ACTIVE, recommendData);
    }
  }

  const recommendationActive = getLocalStorage(RECOMMEND_ACTIVE);
  const preAction = getLocalStorage(PRE_ACTION);
  if (preAction === ACTION.RECOMMEND && recommendationActive.rt === DYNAMIC_BUNDLE_TYPE) {
    actionBundle = true;
    recommendData = recommendationActive;
  }

  const data = { a: action, pid: productId, actionBundle };

  // listener event from quick view
  window.addEventListener('boost-sd-open-quick-view', function bindDataActionToQuickView(e) {
    const quickViewModal = getModalQuickView();

    quickViewModal?.setAttribute('data-action-analytic', data?.a);
    if (e?.detail?.pid) quickViewModal?.setAttribute('data-product-id', e?.detail?.pid);

    window.removeEventListener('boost-sd-open-quick-view', bindDataActionToQuickView);

    checkViewPreOrder(e?.detail?.pid);
  });

  let isSent = false;

  if (
    !isExcludeAddToCart(activeElement) &&
    (activeElement.closest(window.boostAnalytic.addToCart || DEFAULT_SELECTOR.addToCart) ||
      isClickAddToCart(activeElement))
  ) {
    isSent = true;
    if (!data.pid) {
      data.pid = getProductIdFromQuickView(activeElement);
    }

    data.u = USER_ACTION.ADD_TO_CART;
    const actionFromQuickView = getModalQuickView()?.dataset.actionAnalytic;
    if (actionFromQuickView) {
      data.a = actionFromQuickView;
    }
  } else if (activeElement.closest(window.boostAnalytic.buyNow || DEFAULT_SELECTOR.buyNow)) {
    isSent = true;
    if (!data.pid) {
      data.pid = getProductIdFromQuickView(activeElement);
    }

    const actionFromQuickView = getModalQuickView()?.dataset.actionAnalytic;
    if (actionFromQuickView) {
      data.a = actionFromQuickView;
    }

    data.u = USER_ACTION.BUY_NOW;
  } else if (activeElement.closest(window.boostAnalytic.quickView || DEFAULT_SELECTOR.quickView)) {
    isSent = true;

    data.u = USER_ACTION.QUICK_VIEW;
  } else if (activeElement.closest(window.boostAnalytic.recommend || DEFAULT_SELECTOR.recommend)) {
    isSent = true;
  } else if (activeElement.closest(window.boostAnalytic.preOrder || DEFAULT_SELECTOR.preOrder)) {
    isSent = true;
    if (!data.pid) {
      data.pid = getProductIdFromQuickView(activeElement);
    }

    data.u = USER_ACTION.ATC_PRE_ORDER;
    const actionFromQuickView = getModalQuickView()?.dataset.actionAnalytic;
    if (actionFromQuickView) {
      data.a = actionFromQuickView;
    }
  }

  // Place Latest Click into Product Item - assign productId
  if (activeElement.closest(window.boostAnalytic.productItem || DEFAULT_SELECTOR.productItem)) {
    isSent = true;
    const productItem = activeElement.closest(
      window.boostAnalytic.productItem || DEFAULT_SELECTOR.productItem
    );
    const productId = productItem?.dataset?.id || productItem?.getAttribute('id');
    const variantId = productItem?.dataset?.productId; // product-id will is variant-id in turbo version

    data.pid = productId;
    data.vid = variantId || ''; // add variant_id for turbo version

    saveSessionStorageClicked(productId, 'none');
  }

  // if product page get productId from general Settings
  if (!data.pid && window.boostSDData?.product?.id) {
    data.pid = window.boostSDData?.product?.id;
  }

  const isClickPopupBundle =
    activeElement.closest('.boost-sd__recommendation-dynamic-show-popup') ||
    activeElement.closest('.boost-sd__product-item--embedded-bundle') ||
    activeElement.closest('.boost-sd__suggestion-queries-item--embedded-bundle');

  if (isClickPopupBundle) {
    actionBundle = true;
  }

  if (actionBundle) {
    // save last placement of bundle when open popup
    if ((action = ACTION.SUGGEST)) {
      const _placementPopup = PLACEMENT.instant_search;
      setSessionStorage(PopupBundlePlacement, _placementPopup);
    }

    data.placementBundle = getPlacementBundle(placementBundle);
  }

  handleFilterOptionItemButtonClick(activeElement);

  // not build data, sent if isSent = false
  if (!isSent) return;

  if (isSent) {
    const buildData = buildGeneralData(data, { ...recommendData, actionBundle });
    sendEvent(buildData, activeElement);

    if (window.klaviyo && isKlaviyoActive()) {
      handleKlaviyo(activeElement, buildData);
    }
  }
}

/**
 * @type {import('./index.d').isAddedFromOurApp}
 */
const isAddedFromOurApp = (activeElement) => {
  // ignore addToCart of pre-order - TODO Analytic pre-order later.
  if (
    activeElement?.className?.includes?.(`${prefix}button--purchase`) ||
    activeElement?.className?.includes?.(`${prefix}button--preorder`)
  ) {
    return false;
  }

  return activeElement?.className?.includes?.(prefix);
};

/**
 * @type {import('./index.d').isClickAddToCart}
 */
export function isClickAddToCart(activeElement) {
  if (!activeElement) return false;
  /**
   * if not product page then no detection is needed
   * if add from our app then no detection is needed
   * */
  if (!isProductPage() || isAddedFromOurApp(activeElement)) return false;

  /***
   * if not click from our app then not detect button add to cart
   */
  const productId = window.boostSDData?.product?.id;
  const productIdsClicked = getSessionStorage(BOOST_SD_PRODUCT_IDS_CLICKED) || {};
  if (productId && !productIdsClicked[productId]) return false;

  const addToCartKeywords = [
    'add to cart',
    'add-to-cart',
    'add to bag',
    'add_to_cart',
    'addtocart',
    'data-product-form-add',
    'name="add"',
  ];

  const innerText = activeElement?.innerHTML?.toLowerCase() || '';

  return addToCartKeywords.some((keyword) => innerText.includes(keyword));
}

/**
 * @type {import('./index.d').checkSendEventKlaviyoViewCollection}
 */
export function checkSendEventKlaviyoViewCollection() {
  if (isCollectionPage() && window.klaviyo && isKlaviyoActive()) {
    window.klaviyo.push([
      'track',
      KLAVIYO_BOOST_EVENT.VIEW_COLLECTION,
      {
        timestamp: new Date().valueOf(),
        sessionId: window.boostWidgetIntegration.getSessionId(),
        collectionId: window.boostWidgetIntegration?.generalSettings?.collection_id,
        collectionName: window.boostWidgetIntegration?.generalSettings?.collection_handle,
      },
    ]);
  }
}

/**
 * @type {import('./index.d').sendKlaviyoEvent}
 */
export function sendKlaviyoEvent(event) {
  switch (event.u) {
    case USER_ACTION.VIEW_PRODUCT: {
      if (event.pid) {
        if (event.a === ACTION.RECOMMEND && event.wid) {
          const item = {
            timestamp: new Date(event.t).valueOf(),
            sessionId: event.sid,
            productId: String(event.pid),
            variantId: String(event.vid),
            widgetType: event.rt,
            widgetContext: event.pg,
            productName: event.product_name,
            productPrice: event.product_price,
            productUrl: event.product_link,
            productCategory: event.product_category,
            productTags: event.product_tags,
          };
          window.klaviyo.push(['track', KLAVIYO_BOOST_EVENT.CLICK_RECOMMENDATION, item]);
        } else {
          if (event.pg !== 'collection_page') {
            let searchQuery = '';
            if (event.a === ACTION.SUGGEST) {
              const inputsSearch = document.querySelectorAll('.boost-sd__search-widget-init-input');
              for (let i = 0; i < inputsSearch.length; i++) {
                const input = inputsSearch[i];
                if (input?.value) {
                  searchQuery = input.value;
                  break;
                }
              }
            } else if (event.a === ACTION.SEARCH) {
              const searchParams = new URLSearchParams(window.location.search);
              searchQuery = searchParams.get('q');
            }
            const item = {
              searchQuery,
              timestamp: new Date(event.t).valueOf(),
              sessionId: event.sid,
              productId: String(event.pid),
              pathClick: MAP_PATH_CLICK[event.a],
              productName: event.product_name,
              productPrice: event.product_price,
              productUrl: event.product_link,
              productCategory: event.product_category,
              productTags: event.product_tags,
            };
            window.klaviyo.push(['track', KLAVIYO_BOOST_EVENT.CLICK_SEARCH_RESULT, item]);
          } else {
            const item = {
              timestamp: new Date(event.t).valueOf(),
              sessionId: event.sid,
              productId: String(event.pid),
              variantId: String(event.vid),
              productName: event.product_name,
              productPrice: event.product_price,
              productUrl: event.product_link,
              productCategory: event.product_category,
              productTags: event.product_tags,
            };
            window.klaviyo.push(['track', KLAVIYO_BOOST_EVENT.CLICK_PRODUCT, item]);
          }
        }
      }
      break;
    }

    case USER_ACTION.ADD_TO_CART: {
      if (event.actionBundle) {
        try {
          const dynamicBundle = JSON.parse(sessionStorage.DynamicBundleDataSession);
          if (dynamicBundle[event.wid]) {
            const item = {
              timestamp: new Date(event.t).valueOf(),
              sessionId: event.sid,
              pageType: event.pg,
              bundlePrice: dynamicBundle[event.wid]?.totalBundlePrice,
              bundleDiscount: dynamicBundle[event.wid]?.amountValueDisplay,
              bundleProductIds: dynamicBundle[event.wid]?.bundleProductIds,
            };
            window.klaviyo.push(['track', KLAVIYO_BOOST_EVENT.ADD_BUNDLE_TO_CART, item]);
          }
        } catch (e) {
          console.log('Error parsing dynamic bundle');
        }
      } else if (event.pid) {
        const item = {
          timestamp: new Date(event.t).valueOf(),
          sessionId: event.sid,
          productId: String(event.pid),
          variantId: String(event.vid),
          addToCartContext: event.a === ACTION.RECOMMEND ? event.wid : event.pg,
        };

        if (event.product_name) {
          item.productName = event.product_name;
        }
        if (event.product_price) {
          item.productPrice = event.product_price;
        }
        if (event.product_link) {
          item.productUrl = event.product_link;
        }
        if (event.product_category) {
          item.productCategory = event.product_category;
        }
        if (event.product_tags) {
          item.productTags = event.product_tags;
        }
        window.klaviyo.push(['track', KLAVIYO_BOOST_EVENT.ADD_TO_CART, item]);
      }
      break;
    }

    case USER_ACTION.ATC_PRE_ORDER: {
      const item = {
        timestamp: new Date(event.t).valueOf(),
        sessionId: event.sid,
        productId: String(event.pid),
        variantId: String(event.vid),
      };

      const additionalInfo = [
        {
          fieldName: 'product_name',
          mapField: 'productName',
        },
        {
          fieldName: 'product_price',
          mapField: 'productPrice',
        },
        {
          fieldName: 'payment_type',
          mapField: 'paymentType',
        },
        {
          fieldName: 'discount_type',
          mapField: 'discountType',
        },
        {
          fieldName: 'discount_amount',
          mapField: 'discountAmount',
        },
        {
          fieldName: 'quantity',
          mapField: 'quantity',
        },
        {
          fieldName: 'shipping_date',
          mapField: 'shippingDate',
        },
        {
          fieldName: 'final_payment',
          mapField: 'finalPayment',
        },
        {
          fieldName: 'campaign_id',
          mapField: 'campaignId',
        },
      ];

      additionalInfo.forEach((info) => {
        if (event[info.fieldName]) {
          item[info.mapField] = event[info.fieldName];
        }
      });

      window.klaviyo.push(['track', KLAVIYO_BOOST_EVENT.PRE_ORDER, item]);
      break;
    }
    default:
      break;
  }
}

/**
 * @type {import('./index.d').addEventKeyPressForSearching}
 */
export function addEventKeyPressForSearching() {
  let interval;
  let countInterval = 0;

  const handleKeyUp = (e) => {
    if (e.key === 'Enter' && e.target.value && window.klaviyo && isKlaviyoActive()) {
      const item = {
        timestamp: new Date().valueOf(),
        sessionId: window.boostWidgetIntegration.getSessionId(),
        searchQuery: e.target.value,
      };
      window.klaviyo.push(['track', KLAVIYO_BOOST_EVENT.SUBMIT_SEARCH, item]);
    }
  };

  if (window.boostWidgetIntegration?.taeSettings?.instantSearch?.enabled) {
    interval = setInterval(() => {
      // Retry maximum 10 times
      if (countInterval === 10) {
        clearInterval(interval);
        return;
      }

      if (document.querySelector('.boost-sd__search-widget-init-enabled')) {
        clearInterval(interval);
        const inputsSearch = document.querySelectorAll('.boost-sd__search-widget-init-input');

        inputsSearch.forEach((input) => {
          input.addEventListener('keyup', handleKeyUp);
        });
      } else {
        countInterval += 1;
      }
    }, [4000]);
  }
}

/**
 * @type {import('./index.d').sendKlaviyoEventForClickFilterOption}
 */
export function sendKlaviyoEventForClickFilterOption(labelElement) {
  if (labelElement && window.klaviyo && isKlaviyoActive()) {
    const filterOptionList = labelElement.closest('.boost-sd__filter-option');

    if (filterOptionList) {
      const filterOptionTitle = filterOptionList.querySelector('.boost-sd__filter-option-title');
      if (filterOptionTitle) {
        const item = {
          timestamp: new Date().valueOf(),
          sessionId: window.boostWidgetIntegration.getSessionId(),
          filterOptionName: filterOptionTitle.innerText?.trim(),
          filterOptionValue: labelElement.innerText?.trim(),
          collectionId: window.boostWidgetIntegration?.generalSettings?.collection_id,
        };

        window.klaviyo.push(['track', KLAVIYO_BOOST_EVENT.CLICK_FILTER, item]);
      }
    }
  }
}

/**
 * @type {import('./index.d').sendKlaviyoEventInCollectionSearch}
 */
export function sendKlaviyoEventInCollectionSearch(params) {
  if (params?.detail?.searchTerm && window.klaviyo && isKlaviyoActive()) {
    const item = {
      timestamp: new Date().valueOf(),
      sessionId: window.boostWidgetIntegration.getSessionId(),
      searchQuery: params.detail.searchTerm,
    };
    window.klaviyo.push(['track', KLAVIYO_BOOST_EVENT.SUBMIT_SEARCH, item]);
  }
}

/**
 * @type {import('./index.d').sendKlaviyoEventChangeRangeSlider}
 */
export function sendKlaviyoEventChangeRangeSlider(params) {
  if (params?.detail?.min && params?.detail?.max && window.klaviyo && isKlaviyoActive()) {
    const item = {
      timestamp: new Date().valueOf(),
      sessionId: window.boostWidgetIntegration.getSessionId(),
      filterOptionName: params?.detail?.label,
      filterOptionValueMin: params.detail.min,
      filterOptionValueMax: params.detail.max,
      collectionId: window.boostWidgetIntegration?.generalSettings?.collection_id,
    };

    window.klaviyo.push(['track', KLAVIYO_BOOST_EVENT.CLICK_FILTER, item]);
  }
}

/**
 * @type {import('./index.d').identifyUser}
 */
export async function identifyUser() {
  if (window.klaviyo && isKlaviyoActive()) {
    const isIdentified = await window.klaviyo.isIdentified();
    if (!isIdentified && window.boostWidgetIntegration?.app?.staging?.user?.email) {
      window.klaviyo.identify({
        email: window.boostWidgetIntegration?.app?.staging?.user?.email,
        $first_name: window.boostWidgetIntegration?.app?.staging?.user?.first_name || '',
        $last_name: window.boostWidgetIntegration?.app?.staging?.user?.last_name || '',
      });
    }
  }
}

/**
 * @type {import('./index.d').dispatchEventDebounceInCollectionSearch}
 */
export function dispatchEventDebounceInCollectionSearch(searchTerm) {
  window.dispatchEvent(
    new CustomEvent('debounce-in-collection-search', {
      detail: {
        searchTerm,
      },
    })
  );
}

/**
 * @type {import('./index.d').dispatchEventChangeRangeSlider}
 */
export function dispatchEventChangeRangeSlider({ label, min, max }) {
  window.dispatchEvent(
    new CustomEvent('change-range-slider', {
      detail: {
        label,
        min,
        max,
      },
    })
  );
}

/**
 * @type {import('./index.d').dispatchEventReViewCollection}
 */
export function dispatchEventReViewCollection() {
  window.dispatchEvent(new CustomEvent('re-view-collection'));
}

/**
 * @type {import('./index.d').handleFilterOptionItemButtonClick}
 */
export function handleFilterOptionItemButtonClick(activeElement) {
  if (
    activeElement.closest(
      window.boostAnalytic.filterOptionItemButton || DEFAULT_SELECTOR.filterOptionItemButton
    )
  ) {
    if (!activeElement.classList.contains('boost-sd__filter-option-item-button')) {
      setTimeout(() => {
        const parentNode = activeElement.parentNode;
        if (parentNode.classList.contains('boost-sd__filter-option-item-button--selected')) {
          sendKlaviyoEventForClickFilterOption(activeElement);
        }
      }, 0);
    } else {
      setTimeout(() => {
        if (activeElement.classList.contains('boost-sd__filter-option-item-button--selected')) {
          sendKlaviyoEventForClickFilterOption(
            activeElement.querySelector('.boost-sd__filter-option-item-label')
          );
        }
      }, 0);
    }
  }
}

/**
 * @type {import('./index.d').handleKlaviyo}
 */
export function handleKlaviyo(activeElement, buildData) {
  const clonedBuildData = { ...buildData };
  setProductExtraInfo(activeElement, clonedBuildData);
  sendKlaviyoEvent(clonedBuildData);
}

export function setProductExtraInfo(activeElement, buildData) {
  const productItem = activeElement.closest('.boost-sd__product-item');
  const productItemQuickView = activeElement.closest('#boost-sd__modal-quickview');

  try {
    let dataProduct = null;
    let variants = [];
    let variant = null;
    const variantId = getVariantIdFromUrl();

    if (productItem) {
      const productTitle = productItem.querySelector('.boost-sd__product-title');
      const productLink = productItem.querySelector('.boost-sd__product-link');
      const path = productLink ? productLink.getAttribute('href') : '';
      dataProduct = productItem.getAttribute('data-product')
        ? JSON.parse(productItem.getAttribute('data-product'))
        : null;

      buildData.product_name = productTitle?.innerText || '';
      buildData.product_link = `https://${getTenantId()}${path}`;
    } else if (productItemQuickView) {
      const productTitle = productItemQuickView.querySelector('.boost-sd__quick-view-title');
      const productLink = productItemQuickView.querySelector('.boost-sd__quick-view-details-link');
      const path = productLink ? productLink.getAttribute('href') : '';
      dataProduct = productItemQuickView.getAttribute('data-product')
        ? JSON.parse(productItemQuickView.getAttribute('data-product'))
        : null;

      buildData.product_name = productTitle?.innerText || '';
      buildData.product_link = `https://${getTenantId()}${path}`;
    }

    if (dataProduct) {
      buildData.product_price = Number(dataProduct.priceMin);
      buildData.product_category = dataProduct.category || '';
      buildData.product_tags = dataProduct.tags || [];

      if (dataProduct.variants) {
        variants =
          typeof dataProduct.variants === 'string'
            ? JSON.parse(dataProduct.variants)
            : dataProduct.variants;
      }
    } else if (isProductPage() && window.boostSDData) {
      buildData.product_price = window.boostSDData?.product?.price;
      buildData.product_tags = window.boostSDData?.product?.tags || [];
      if (window.boostSdPreOrderData) {
        variants = window.boostSdPreOrderData?.variants;
      }
    }

    if (variants.length) {
      variant = variantId
        ? variants.find((item) => +item.id === +variantId)
        : buildData.vid
          ? variants.find((item) => +item.id === +buildData.vid)
          : variants[0];
    }

    if (!buildData.vid && variant) {
      buildData.vid = variant.id;
    }

    if (variant?.preorder) {
      buildData.payment_type = variant?.preorder?.payment?.type || '';
      buildData.discount_type = variant?.preorder?.payment?.discountType || '';
      buildData.discount_amount = +variant?.preorder?.payment?.discountValue || '';
      buildData.shipping_date = variant?.preorder?.payment?.shippingDate
        ? new Date(variant?.preorder?.payment?.shippingDate).valueOf() / 1000
        : '';
      buildData.final_payment = variant?.preorder?.payment?.finalPayment?.value || '';
      buildData.campaign_id = variant?.preorder?.settings?.campaignId || '';
      const inputQuantity = productItemQuickView
        ? productItemQuickView.querySelector('.boost-sd__input-number-input')
        : document.querySelector('.boost-sd__input-number-input');

      if (inputQuantity) {
        buildData.quantity = +inputQuantity?.value || 1;
      } else {
        buildData.quantity = 1;
      }
    }
  } catch (e) {
    console.log('error', e);
  }
}
